import { gql, useQuery } from "urql";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { useComparisonModeContext, useCurrencyContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import { Kpi, StandardPeriod as DomainStandardPeriod } from "@acto/taskpane/core/domain";
import {
  Granularity as GqlGranularity,
  Kpi as GqlKpi,
  KpiComparisonMode as GqlKpiComparisonMode,
} from "@acto/gql/graphql";
import {
  toGqlGranularity,
  toGqlKpi,
  toGqlKpiComparisonMode,
  toGqlPeriod,
} from "@acto/taskpane/core/data-access/customers/kpiTransformers";
import { buildKpiTrend, type KpiSummaryValueData, type KpiTrend } from "./buildKpiTrend";
import { selectGranularity } from "./selectGranularity";

const TenantKpiTrendQuery = gql`
  query GetTenantKpiTrend(
    $kpis: [Kpi!]!
    $period: StandardPeriod
    $dateRange: DateRangeInput
    $granularity: Granularity!
    $productGroupId: ID
    $comparisonMode: KpiComparisonMode!
  ) {
    kpiSummaries(
      kpis: $kpis
      period: $period
      dateRange: $dateRange
      granularity: $granularity
      productGroupId: $productGroupId
      comparisonMode: $comparisonMode
    ) {
      granularity
      values {
        kpi
        totalValue
        timeSeries {
          dataPoints {
            periodStart
            value
          }
        }
        comparison {
          totalValue
          change {
            percentageChange
          }
          range {
            startDate
            endDate
          }
        }
        comparisonTimeSeries {
          dataPoints {
            periodStart
            value
          }
        }
      }
    }
  }
`;

interface TenantKpiTrendQueryData {
  kpiSummaries: { granularity: GqlGranularity; values: KpiSummaryValueData[] } | null;
}

interface TenantKpiTrendQueryVariables {
  kpis: GqlKpi[];
  period?: ReturnType<typeof toGqlPeriod>;
  dateRange?: { startDate: string; endDate: string };
  granularity: ReturnType<typeof toGqlGranularity>;
  productGroupId?: string;
  comparisonMode: GqlKpiComparisonMode;
}

export interface TenantKpiTrendOptions {
  kpi?: Kpi;
  period?: DomainStandardPeriod | null;
  dateRange?: { startDate: string; endDate: string } | null;
  productGroupId?: string;
  isCustomRange?: boolean;
}

/**
 * Revenue trend without a client: the server scopes it to the caller's access context (a sales
 * employee sees their own clients).
 */
export function useTenantKpiTrend({
  kpi = Kpi.REVENUE,
  period,
  dateRange,
  productGroupId,
  isCustomRange = false,
}: TenantKpiTrendOptions): KpiTrend {
  const { locale } = useLocale();
  const currency = useCurrencyContext();
  const tKpi = useTranslations("kpi");

  const isMargin = kpi === Kpi.MARGIN;
  const displayKpi = isMargin ? Kpi.PROFIT : kpi;
  const kpisToFetch = isMargin ? [GqlKpi.Profit, GqlKpi.Margin] : [toGqlKpi(displayKpi)];
  const comparisonMode = useComparisonModeContext();

  const granularity = selectGranularity(dateRange, period ?? undefined);

  const [result] = useQuery<TenantKpiTrendQueryData, TenantKpiTrendQueryVariables>({
    query: TenantKpiTrendQuery,
    variables: {
      kpis: kpisToFetch,
      period: dateRange ? undefined : period ? toGqlPeriod(period) : undefined,
      dateRange: dateRange ?? undefined,
      granularity: toGqlGranularity(granularity),
      productGroupId: productGroupId ?? undefined,
      comparisonMode: toGqlKpiComparisonMode(comparisonMode),
    },
    requestPolicy: "cache-first",
  });

  return buildKpiTrend({
    values: result.data?.kpiSummaries?.values ?? [],
    fetching: result.fetching,
    stale: result.stale,
    selectedKpi: kpi,
    comparisonMode,
    dateRange,
    isCustomRange,
    granularity,
    locale,
    currency,
    tKpi,
  });
}
